import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, X, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { listProfiles, createProfile } from "@/lib/api";

const AVATARS = ["🦊", "🐼", "🦄", "🐯", "🐸", "🐵", "🐙", "🦖"];
const COLORS = ["#3B82F6", "#EF4444", "#22C55E", "#A855F7", "#F59E0B", "#EC4899"];

const HERO_IMG = "/hero-kids.jpg";

export default function ProfilesPage({ onSelect }) {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [avatar, setAvatar] = useState(AVATARS[0]);
  const [color, setColor] = useState(COLORS[0]);
  const [saving, setSaving] = useState(false);

  const load = () => {
    listProfiles()
      .then(setProfiles)
      .catch(() => toast.error("Could not load profiles"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const pick = (p) => {
    onSelect(p);
    navigate("/home");
  };

  const submit = async () => {
    if (!name.trim()) {
      toast.error("Please add a name");
      return;
    }
    setSaving(true);
    try {
      const p = await createProfile({ name: name.trim(), avatar, color });
      pick(p);
    } catch (e) {
      toast.error("Could not create profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="app-shell lg:grid lg:grid-cols-2">
      {/* Left: warm human learning scene */}
      <motion.aside
        initial={{ opacity: 0, x: -30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ type: "spring", stiffness: 120, damping: 18 }}
        className="relative hidden lg:flex flex-col justify-end p-12 m-6 rounded-[2.5rem] overflow-hidden border-4 border-white"
        style={{ boxShadow: "0 14px 0 0 rgba(37,99,235,0.35)" }}
        data-testid="welcome-hero"
      >
        <img
          src={HERO_IMG}
          alt="A young child stacking colorful alphabet learning blocks"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/25 to-transparent" />
        <div className="relative z-10 text-white max-w-md">
          <span className="inline-flex items-center gap-2 bg-yellow-400 text-slate-900 font-bold text-sm uppercase tracking-widest px-4 py-2 rounded-full mb-5">
            <Sparkles size={16} strokeWidth={3} /> Preschool · Ages 3–5
          </span>
          <h2 className="font-fredoka text-4xl xl:text-5xl font-bold leading-tight">
            Letters, numbers, animals & colors
          </h2>
          <p className="text-lg xl:text-xl font-bold text-white/80 mt-4">
            A cozy little place where curious kids learn by tapping, listening
            and giggling along.
          </p>
        </div>
      </motion.aside>

      {/* Right: profile selection */}
      <div className="dotted-bg flex flex-col items-center justify-center px-6 py-14 min-h-screen">
        <motion.div
          initial={{ scale: 0.85, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 15 }}
          className="text-center mb-10"
        >
          <img
            src="/logo.png"
            alt="TinySpark Learning logo"
            data-testid="app-logo"
            className="w-[258px] sm:w-[294px] mx-auto mb-2 rounded-[2rem] shadow-xl border-4 border-white"
          />
          <p className="text-xl sm:text-2xl font-bold text-slate-500 mt-3">
            Who is playing today?
          </p>
        </motion.div>

        {loading ? (
          <Loader2 className="animate-spin text-blue-500" size={48} strokeWidth={3} />
        ) : (
          <div
            className="grid grid-cols-2 sm:grid-cols-3 gap-6 max-w-2xl w-full"
            data-testid="profiles-grid"
          >
            {profiles.map((p, i) => (
              <motion.button
                key={p.id}
                data-testid={`profile-card-${p.id}`}
                onClick={() => pick(p)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05, type: "spring" }}
                whileHover={{ y: -8 }}
                className="toy-btn flex flex-col items-center gap-3 bg-white rounded-3xl border-4 p-6"
                style={{ borderColor: p.color, boxShadow: `0 8px 0 0 ${p.color}` }}
              >
                <div
                  className="w-24 h-24 rounded-full flex items-center justify-center text-5xl border-4 border-white"
                  style={{ backgroundColor: `${p.color}22` }}
                >
                  {p.avatar}
                </div>
                <span className="font-fredoka text-2xl font-bold text-slate-800 truncate max-w-full">
                  {p.name}
                </span>
              </motion.button>
            ))}

            <motion.button
              data-testid="add-profile-button"
              onClick={() => setShowForm(true)}
              whileHover={{ y: -8 }}
              className="toy-btn flex flex-col items-center justify-center gap-3 bg-slate-100 rounded-3xl border-4 border-dashed border-slate-300 p-6 min-h-[180px]"
            >
              <div className="w-20 h-20 rounded-full bg-blue-500 flex items-center justify-center">
                <Plus size={44} strokeWidth={3} className="text-white" />
              </div>
              <span className="font-fredoka text-xl font-bold text-slate-500">
                Add Kid
              </span>
            </motion.button>
          </div>
        )}
      </div>

      <AnimatePresence>
        {showForm && (
          <motion.div
            className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="new-profile-modal"
          >
            <motion.div
              initial={{ scale: 0.8, y: 30 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", stiffness: 220, damping: 18 }}
              className="bg-white rounded-[2rem] border-4 border-slate-900/10 p-8 w-full max-w-md"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-fredoka text-3xl font-bold text-slate-900">
                  New Kid
                </h2>
                <button
                  data-testid="close-modal-button"
                  onClick={() => setShowForm(false)}
                  className="w-11 h-11 rounded-full bg-slate-100 flex items-center justify-center"
                >
                  <X size={24} strokeWidth={3} className="text-slate-500" />
                </button>
              </div>

              <input
                data-testid="profile-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Name"
                maxLength={16}
                className="w-full text-2xl font-bold font-fredoka bg-slate-100 rounded-2xl px-5 py-4 outline-none border-4 border-transparent focus:border-blue-400 mb-6"
              />

              <p className="text-sm font-bold uppercase tracking-widest text-slate-400 mb-3">
                Pick a buddy
              </p>
              <div className="grid grid-cols-4 gap-3 mb-6">
                {AVATARS.map((a) => (
                  <button
                    key={a}
                    data-testid={`avatar-option-${a}`}
                    onClick={() => setAvatar(a)}
                    className={`aspect-square rounded-2xl text-3xl flex items-center justify-center border-4 transition-transform ${
                      avatar === a
                        ? "border-blue-500 bg-blue-50 scale-105"
                        : "border-transparent bg-slate-100"
                    }`}
                  >
                    {a}
                  </button>
                ))}
              </div>

              <p className="text-sm font-bold uppercase tracking-widest text-slate-400 mb-3">
                Pick a color
              </p>
              <div className="flex gap-3 mb-8">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    data-testid={`color-option-${c}`}
                    onClick={() => setColor(c)}
                    className={`w-11 h-11 rounded-full border-4 ${
                      color === c ? "border-slate-900" : "border-white"
                    }`}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>

              <button
                data-testid="save-profile-button"
                onClick={submit}
                disabled={saving}
                className="toy-btn w-full bg-green-500 text-white font-fredoka text-2xl font-bold py-4 rounded-2xl flex items-center justify-center gap-2"
                style={{ boxShadow: "0 6px 0 0 rgba(21,128,61,1)" }}
              >
                {saving ? <Loader2 className="animate-spin" /> : "Let's Go!"}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
